import { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Facebook, Instagram, Youtube, Mail, Phone, MapPin } from 'lucide-react'
import { useLanguageStore } from '../../lib/store'

interface Settings {
    contact_address?: string
    contact_phone?: string
    contact_email?: string
    social_facebook?: string
    social_instagram?: string
    social_twitter?: string
    social_youtube?: string
    social_tiktok?: string
}

export default function Footer() {
    const { t } = useTranslation()
    const { language } = useLanguageStore()
    const [settings, setSettings] = useState<Settings>({})

    useEffect(() => {
        const fetchSettings = async () => {
            try {
                const response = await fetch('/api/settings/public')
                const data = await response.json()
                if (data.success && data.data) {
                    const settingsObj: Settings = {}
                    data.data.forEach((s: { key: string, value: string }) => {
                        settingsObj[s.key as keyof Settings] = s.value
                    })
                    setSettings(settingsObj)
                }
            } catch (error) {
                console.error('Failed to load settings:', error)
            }
        }
        fetchSettings()
    }, [])

    return (
        <footer className="bg-gray-900 text-gray-300">
            <div className="container mx-auto px-4 py-12">
                <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
                    {/* About */}
                    <div>
                        <h3 className="text-white font-bold text-lg mb-4">Phú Quốc Rental</h3>
                        <p className="text-sm mb-4">
                            {language === 'en'
                                ? 'Your trusted partner for vehicle rentals and tours in Phú Quốc. Explore the island with ease.'
                                : 'Đối tác tin cậy cho thuê xe và tour tại Phú Quốc. Khám phá đảo một cách dễ dàng.'}
                        </p>
                        <div className="flex space-x-4">
                            {settings.social_facebook && (
                                <a href={settings.social_facebook} target="_blank" rel="noopener noreferrer" className="hover:text-white transition">
                                    <Facebook className="w-5 h-5" />
                                </a>
                            )}
                            {settings.social_instagram && (
                                <a href={settings.social_instagram} target="_blank" rel="noopener noreferrer" className="hover:text-white transition">
                                    <Instagram className="w-5 h-5" />
                                </a>
                            )}
                            {settings.social_youtube && (
                                <a href={settings.social_youtube} target="_blank" rel="noopener noreferrer" className="hover:text-white transition">
                                    <Youtube className="w-5 h-5" />
                                </a>
                            )}
                        </div>
                    </div>

                    {/* Quick Links */}
                    <div>
                        <h3 className="text-white font-bold text-lg mb-4">
                            {language === 'en' ? 'Quick Links' : 'Liên kết nhanh'}
                        </h3>
                        <ul className="space-y-2 text-sm">
                            <li>
                                <Link to="/vehicles" className="hover:text-white transition">
                                    {t('vehicles')}
                                </Link>
                            </li>
                            <li>
                                <Link to="/tours" className="hover:text-white transition">
                                    {t('tours')}
                                </Link>
                            </li>
                            <li>
                                <Link to="/blog" className="hover:text-white transition">
                                    {t('blog')}
                                </Link>
                            </li>
                            <li>
                                <Link to="/contact" className="hover:text-white transition">
                                    {language === 'en' ? 'Contact' : 'Liên hệ'}
                                </Link>
                            </li>
                        </ul>
                    </div>

                    {/* Support */}
                    <div>
                        <h3 className="text-white font-bold text-lg mb-4">
                            {language === 'en' ? 'Support' : 'Hỗ trợ'}
                        </h3>
                        <ul className="space-y-2 text-sm">
                            <li>
                                <Link to="/about" className="hover:text-white transition">
                                    {language === 'en' ? 'About Us' : 'Về chúng tôi'}
                                </Link>
                            </li>
                            <li>
                                <Link to="/terms" className="hover:text-white transition">
                                    {language === 'en' ? 'Terms of Service' : 'Điều khoản dịch vụ'}
                                </Link>
                            </li>
                            <li>
                                <Link to="/privacy" className="hover:text-white transition">
                                    {language === 'en' ? 'Privacy Policy' : 'Chính sách bảo mật'}
                                </Link>
                            </li>
                            <li>
                                <Link to="/faq" className="hover:text-white transition">
                                    {language === 'en' ? 'FAQ' : 'Hỏi đáp'}
                                </Link>
                            </li>
                        </ul>
                    </div>

                    {/* Contact - Dynamic from settings */}
                    <div>
                        <h3 className="text-white font-bold text-lg mb-4">
                            {language === 'en' ? 'Contact' : 'Liên hệ'}
                        </h3>
                        <ul className="space-y-3 text-sm">
                            {settings.contact_address && (
                                <li className="flex items-start space-x-2">
                                    <MapPin className="w-5 h-5 flex-shrink-0 mt-0.5" />
                                    <span>{settings.contact_address}</span>
                                </li>
                            )}
                            {settings.contact_phone && (
                                <li className="flex items-center space-x-2">
                                    <Phone className="w-5 h-5 flex-shrink-0" />
                                    <a href={`tel:${settings.contact_phone}`} className="hover:text-white transition">
                                        {settings.contact_phone}
                                    </a>
                                </li>
                            )}
                            {settings.contact_email && (
                                <li className="flex items-center space-x-2">
                                    <Mail className="w-5 h-5 flex-shrink-0" />
                                    <a href={`mailto:${settings.contact_email}`} className="hover:text-white transition">
                                        {settings.contact_email}
                                    </a>
                                </li>
                            )}
                        </ul>
                    </div>
                </div>

                <div className="border-t border-gray-800 mt-8 pt-8 text-center text-sm">
                    <p>&copy; {new Date().getFullYear()} Phú Quốc Rental. {language === 'en' ? 'All rights reserved.' : 'Bản quyền thuộc về Phú Quốc Rental.'}</p>
                </div>
            </div>
        </footer>
    )
}
